import StreamingAvatar, {
  TaskType,
  TaskMode,
  StartAvatarResponse,
  AvatarQuality,
  StreamingEvents,
  VoiceEmotion,
} from "@heygen/streaming-avatar";
import { useMemoizedFn, usePrevious } from "ahooks";
import { useMicVAD } from "@ricky0123/vad-react";
import { CONST } from "lib/const";
import { useIsDesktop } from "lib/device";
import {
  useAccessGaiaTenant,
  useAvatarLogChat,
  useGetAvatarClick,
  useGetAvatarLogChat,
  useGetAvatarLogChatBySessionId,
  useGetSubdomainData,
  useRequestAvatarQuota,
} from "modules/Tenant/homeTenantApi";
import React, {
  createContext,
  Dispatch,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
} from "react";
import { useEffect, useRef, useState } from "react";
import { useAvatarStore } from "./avatarStore";
import { AvatarMobile } from "./AvatarMobile";
import { AvatarDesktop } from "./AvatarDesktop";
import { useToastStore } from "components/Toast/toastStore";
import { isBrowser } from "lib/ssr";
import { getSubDomain, isMalukuURL } from "lib/host";
import { PROMPT_7 } from "./quizPrompt";
import { useAuthStore } from "modules/Auth/authStore";
import { trackEventGA, trackEventGTM } from "lib/amplitude";
import { useMediaStore as useMedia } from "modules/Course/mediaStore";
import { useMediaStore } from "@vidstack/react";
import { useRouter } from "next/router";
import logger from "lib/Logger";
import { Room, RoomEvent, VideoPresets } from "livekit-client";
import * as awsTranscribe from "lib/aws-transcribe.utils";
import { AvatarData, ChatType, GetChatType, GetSummaryType } from "./types";
import { INTRO } from "./openingIntro";
import { AvatarDesktopChat } from "./AvatarDesktopChat";
import { generateUUIDv1 } from "lib/generateUUIDv1";
import { AvatarMobileChat } from "./AvatarMobileChat";

const WS_URL = CONST.WS_URL;
const WS_INPUT_URL = CONST.WS_INPUT_URL;
const HEYGEN_API_URL = CONST.HEYGEN_API_URL;

export const VisualAudioContext = createContext(false);

export const InteractiveAvatar = forwardRef<
  { handleInterrupt: () => Promise<void> },
  {
    isCourse?: boolean;
    isVisible: boolean;
    currentUser: any;
    isSmaller?: boolean;
    setIsSmaller?: Dispatch<SetStateAction<boolean>>;
    isAnyFiltered?: boolean;
    isKeywordsSearched?: boolean;
    isMyClass?: boolean;
  }
>(
  (
    {
      isCourse = false,
      isVisible,
      currentUser,
      isSmaller,
      setIsSmaller,
      isAnyFiltered = false,
      isKeywordsSearched = false,
      isMyClass = false,
    },
    ref
  ) => {
    const router = useRouter();
    const subDomain = isBrowser()
      ? getSubDomain(window.location.hostname)
      : null;
    const showToast = useToastStore((state) => state.showToast);
    const { setStreamAvatar, setDataAvatarGlobal } = useAvatarStore();
    const isDesktop = useIsDesktop();

    const { player, playerVideoJs, isPlayerReady } = useMedia();
    const media = useMediaStore(player);

    const { isScrolled, setIsScrolled } = useAvatarStore();
    // const [isLoadingSession, setIsLoadingSession] = useState(false);
    const { isLoadingSession, setIsLoadingSession } = useAvatarStore();
    const [isCloseWhenLoading, setIsCloseWhenLoading] = useState(false);
    const [isLoadingRepeat, setIsLoadingRepeat] = useState(false);
    const [stream, setStream] = useState<MediaStream>();
    const [debug, setDebug] = useState<string>();
    const [dataAvatar, setDataAvatar] = useState<StartAvatarResponse>();
    const [text, setText] = useState<string>("");
    const [textQuiz, setTextQuiz] = useState<string>(PROMPT_7);
    const mediaStream = useRef<HTMLVideoElement>(null);
    const avatar = useRef<StreamingAvatar | null>(null);
    const { setAvatar } = useAvatarStore();
    const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
    const [micIsLoading, setMicIsLoading] = useState(false);
    const [modeIsLoading, setModeIsLoading] = useState(false);
    const [disabledMic, setDisabledMic] = useState(false);

    const [chatMode, setChatMode] = useState("voice_mode");
    const [isUserTalking, setIsUserTalking] = useState(false);
    const [textValue, setTextValue] = useState<string>("");

    const [isIntro, setIsIntro] = useState(false);
    const [avatarMessage, setAvatarMessage] = useState<string[]>([]);
    const [avatarEndMessage, setAvatarEndMessage] = useState(false);
    const [userMessage, setUserMessage] = useState<string[]>([]);
    const [userEndMessage, setUserEndMessage] = useState(false);
    const [isSaveChat, setIsSaveChat] = useState(false);
    const [saveChat, setSaveChat] = useState<ChatType>({
      session_id: "",
      start_chat: null,
      end_chat: null,
      chat: [],
    });
    const [chat, setChat] = useState<GetChatType>({
      chat: [],
    });
    const [summaryRecap, setSummaryRecap] = useState<GetSummaryType>({
      summary: [],
    });

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);

    // NXTX Integration
    const [passkey, setPasskey] = useState("esdm-gk");
    const [isPasskeyValid, setIsPasskeyValid] = useState(false);
    const mediaElementRef = useRef<HTMLVideoElement | null>(null);
    const [streamNxtx, setStreamNxtx] = useState(false);
    const [isUserTalkingNxtx, setIsUserTalkingNxtx] = useState(false);
    const [avatarMessageNxtx, setAvatarMessageNxtx] = useState<string>("");
    // const [isLoadingSessionNxtx, setIsLoadingSessionNxtx] = useState(false);
    const { isLoadingSessionNxtx, setIsLoadingSessionNxtx } = useAvatarStore();
    const [isRunning, setIsRunning] = useState(false);
    const [sessionId, setSessionId] = useState<string>("");
    const [avatarSlug, setAvatarSlug] = useState<string>("");
    const [sessionInfo, setSessionInfo] = useState<any>(null);
    const [selectedRecapId, setSelectedRecapId] = useState<string | null>(null);
    const [newChatRoom, setNewChatRoom] = useState<boolean>(true);
    const [isNewChatRoomLoading, setIsNewChatRoomLoading] = useState(false);
    const [isReadyToFetch, setIsReadyToFetch] = useState(false);
    const analyticSocket = useRef<WebSocket | null>(null);
    const taskSocket = useRef<WebSocket | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const hasSessionStarted = useRef(false);
    const [transcript, setTranscript] = useState<string>("");
    const transcribeInitialized = useRef(false);
    const transcribeAbortController = useRef<AbortController | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioMediaStreamRef = useRef<MediaStream | null>(null);
    let room: any = null;
    let mediaStreamNxtx: any = null;

    const vad = useMicVAD({
      model: "v5",
      baseAssetPath: "/",
      onnxWASMBasePath: "/",
      onSpeechStart: () => {
        if (streamNxtx && !chatModeTenant) {
          sendInterrupt();
        }
      },
    });

    const nxtxTenant =
      (currentUser?.tenant !== undefined &&
        [
          "gok-ares-enterprise-plus",
          "gok-ares-preview",
          "esdm-ares",
          "esdm",
          "ppsdmaparatur",
        ].includes((currentUser?.tenant as any)?.slug)) ||
      [
        "gok-ares-enterprise-plus",
        "gok-ares-preview",
        "esdm-ares",
        "esdm",
        "ppsdmaparatur",
      ].includes(currentUser?.referral);

    const chatModeTenant =
      (currentUser?.tenant !== undefined &&
        ["gok-ares-preview", "esdm-ares", "esdm"].includes(
          (currentUser?.tenant as any)?.slug
        )) ||
      ["gok-ares-preview", "esdm-ares", "esdm"].includes(currentUser?.referral);

    const handlePasskeySubmit = async (e?: React.FormEvent) => {
      e?.preventDefault();

      try {
        const response = await fetch("/api/nxtx/auth/verify-passkey", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ passkey: passkey, type: "passkey" }),
        });

        if (response.ok) {
          // setPasskey("");

          const data = await response.json();

          const expirationTime = new Date().getTime() + 3600000;

          const avatarData = {
            data: data.data,
            expiration: expirationTime,
          };
          localStorage.setItem("avatarData", JSON.stringify(avatarData));

          setIsPasskeyValid(true);
          startSessionNxtx(avatarData.data);
        } else {
          const error = await response.json();
          showToast({
            type: "danger",
            message: error.detail.error,
          });
        }
      } catch (error) {
        console.error("Error verifying passkey:", error);
        showToast({
          type: "danger",
          message: "An error occurred. Please try again.",
        });
      }
    };

    async function initializeWebSocket() {
      analyticSocket.current = new WebSocket(`${WS_URL}/ws/analytics`);

      if (
        analyticSocket.current &&
        analyticSocket.current.readyState === analyticSocket.current.OPEN
      ) {
        logger.debug("WebSocket Analytics is already open.");
        return;
      }

      analyticSocket.current.onopen = () => {
        logger.debug("WebSocket Analytics connected.");
      };

      analyticSocket.current.onmessage = (event: any) => {
        logger.debug("Message from server:", event.data);
      };

      analyticSocket.current.onerror = (error: any) => {
        logger.debug("WebSocket Analytics error:", error);
      };

      analyticSocket.current.onclose = () => {
        analyticSocket.current = null; // Reset the socket to allow reconnection
      };
    }

    async function initializeTaskWebSocket(slug: any, sessionId: any) {
      taskSocket.current = new WebSocket(
        `${WS_INPUT_URL}/ws/tasks/${slug}/${sessionId}`
      );

      if (
        taskSocket.current &&
        taskSocket.current.readyState === taskSocket.current.OPEN
      ) {
        logger.debug("WebSocket Task is already open.");
        return;
      }

      taskSocket.current.onopen = () => {
        logger.debug("WebSocket Task connected.");
      };

      taskSocket.current.onmessage = (event: any) => {
        logger.debug("Message from server:", event.data);
        const jsonData = JSON.parse(event.data);
        if (jsonData.user && jsonData.avatar) {
          if (newChatRoom) {
            setIsReadyToFetch(true);
          }

          setSaveChat((prev: any) => ({
            ...prev,
            chat: [
              ...prev.chat,
              {
                user: jsonData.user,
                avatar: jsonData.avatar,
              },
            ],
          }));
          setChat((prev: any) => ({
            ...prev,
            chat: [
              ...prev.chat,
              {
                user: jsonData.user,
                avatar: jsonData.avatar,
              },
            ],
          }));
        }
      };

      taskSocket.current.onerror = (error: any) => {
        logger.debug("WebSocket Task error:", error);
      };

      taskSocket.current.onclose = () => {
        taskSocket.current = null; // Reset the socket to allow reconnection
      };
    }

    async function sendCheckpoint(checkpointData: any = {}) {
      if (
        analyticSocket.current &&
        analyticSocket.current.readyState === WebSocket.OPEN
      ) {
        analyticSocket.current.send(JSON.stringify(checkpointData));
      } else {
        logger.error("WebSocket is not ready. Failed to send checkpoint data.");
      }
    }

    async function createSession({
      avatar_id = null,
      voice_id = null,
      type = null,
    }: {
      avatar_id?: any;
      voice_id?: any;
      type?: string | null;
    }) {
      try {
        const response = await fetch("/api/nxtx/auth/create-session", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...(avatar_id && { avatar_id }),
            ...(voice_id && { voice_id }),
            ...(type && { type }),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        logger.error("Error fetching access token:", error);
      }

      return false;
    }

    async function closeSession(session_id: any) {
      try {
        const response = await fetch("/api/nxtx/auth/close-session", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_id: session_id,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        logger.error("Error fetching access token:", error);
      }

      return "";
    }

    async function sendInterrupt() {
      try {
        if (
          taskSocket.current &&
          taskSocket.current.readyState === WebSocket.OPEN
        ) {
          const message = JSON.stringify({
            type: "interrupt",
          });
          taskSocket.current.send(message);
        } else {
          logger.error("WebSocket is not ready. Failed to send task.");
        }
      } catch (error) {
        logger.error("Error send task:", error);
      }
    }

    const [lastSentMessage, setLastSentMessage] = useState<{
      text: string;
      type: string;
    } | null>(null);
    const [isFirstAttempt, setIsFirstAttempt] = useState(false);

    useEffect(() => {
      if (lastSentMessage && chatModeTenant && !isFirstAttempt) {
        const timer = setTimeout(() => {
          sendText(lastSentMessage.text, lastSentMessage.type);
          // Clear the last sent message after sending
          setLastSentMessage(null);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }, [lastSentMessage, isFirstAttempt]);

    async function sendText(text: any, type: any = "task") {
      try {
        if (
          taskSocket.current &&
          taskSocket.current.readyState === WebSocket.OPEN
        ) {
          // Create message object first to inspect it
          const messageObj = {
            type: type,
            ...(chatModeTenant && { session_type: "text" }),
            message: text,
          };

          // Log the message object before sending
          logger.debug("Preparing to send message:", messageObj);

          const message = JSON.stringify(messageObj);
          taskSocket.current.send(message);
          logger.debug("Message sent successfully");

          // Store the message to be sent again via useEffect, but only if it's not already a second send
          if (!lastSentMessage && chatModeTenant) {
            setLastSentMessage({ text, type });
          }

          // Add a ping after sending to check if connection is still alive
          setTimeout(() => {
            if (taskSocket.current?.readyState === WebSocket.OPEN) {
              logger.debug("WebSocket still open after sending message");
            } else {
              logger.error("WebSocket closed after sending message");
              // Attempt to reconnect if needed
              if (
                chatModeTenant &&
                taskSocket.current?.readyState !== WebSocket.OPEN
              ) {
                logger.debug("Attempting to reconnect WebSocket");
                initializeTaskWebSocket(avatarSlug, sessionId);
              }
            }
          }, 500);
        } else {
          logger.error("WebSocket is not ready. Failed to send task.");

          // Attempt to reconnect
          if (chatModeTenant) {
            logger.debug("Attempting to reconnect WebSocket before sending");
            await initializeTaskWebSocket(avatarSlug, sessionId);

            // Try sending again after reconnection
            setTimeout(() => {
              if (taskSocket.current?.readyState === WebSocket.OPEN) {
                const retryMessage = JSON.stringify({
                  type: type,
                  ...(chatModeTenant && { session_type: "text" }),
                  message: text,
                });
                taskSocket.current.send(retryMessage);
                logger.debug("Message sent after reconnection");

                // Also set the message to be sent again after reconnection
                if (!lastSentMessage && chatModeTenant) {
                  setLastSentMessage({ text, type });
                }
              }
            }, 1000);
          }
        }
      } catch (error) {
        logger.error("Error send task:", error);

        // Log detailed error information
        if (error instanceof Error) {
          logger.error("Error details:", error.message, error.stack);
        }
      } finally {
        setTextValue("");
        setIsFirstAttempt(false);
      }
    }

    const getAvatarData = () => {
      const storedData = localStorage.getItem("avatarData");

      if (storedData) {
        const avatarData = JSON.parse(storedData);

        if (new Date().getTime() > avatarData.expiration) {
          localStorage.removeItem("avatarData");
          return null;
        }

        return avatarData.data;
      }

      return null;
    };

    useEffect(() => {
      if (!sessionInfo) return;

      const setupRoomAndStartSession = async () => {
        try {
          // Initialize LiveKit Room
          room = new Room({
            adaptiveStream: true,
            dynacast: true,
            videoCaptureDefaults: {
              resolution: VideoPresets.h720.resolution,
            },
          });

          room.on(RoomEvent.Connected, () => {
            logger.debug("Connected");
            setStreamNxtx(true);
            setIsLoadingSessionNxtx(false);
          });

          room.on(RoomEvent.Disconnected, () => {
            // endSessionNxtx();
          });

          room.on(RoomEvent.DataReceived, (message: any) => {
            const data = new TextDecoder().decode(message);
            const jsonData = JSON.parse(data);
            logger.debug("Room message:", jsonData);
            jsonData?.message && setAvatarMessage(jsonData.message);
          });

          mediaStreamNxtx = new MediaStream();
          room.on(RoomEvent.TrackSubscribed, (track: any) => {
            if (track.kind === "video" || track.kind === "audio") {
              mediaStreamNxtx.addTrack(track.mediaStreamTrack);
              if (mediaElementRef.current) {
                // Set source object only once
                if (!mediaElementRef.current.srcObject) {
                  mediaElementRef.current.srcObject = mediaStreamNxtx;
                }

                // Play as soon as we have either video or audio
                if (mediaStreamNxtx.getTracks().length > 0) {
                  setIsRunning(true);
                  // Add autoplay attribute
                  mediaElementRef.current.autoplay = true;
                  mediaElementRef.current.play().catch((error) => {
                    logger.error("Error playing video:", error);
                    // Retry play on user interaction
                    const playOnInteraction = () => {
                      mediaElementRef.current?.play();
                      document.removeEventListener("click", playOnInteraction);
                    };
                    document.addEventListener("click", playOnInteraction);
                  });
                }
              }
            }
          });

          room.on(RoomEvent.TrackUnsubscribed, (track: any) => {
            const mediaTrack = track.mediaStreamTrack;
            if (mediaTrack) {
              mediaStreamNxtx.removeTrack(mediaTrack);
            }
          });

          room.on(RoomEvent.Disconnected, (reason: any) => {
            logger.debug(`Room disconnected: ${reason}`);
          });

          await room.prepareConnection(
            sessionInfo.url,
            sessionInfo.access_token
          );
          await room.connect(sessionInfo.url, sessionInfo.access_token);
        } catch (error) {
          logger.error("Error in setupRoomAndStartSession:", error);
        }
      };

      if (sessionInfo && !chatModeTenant) {
        setupRoomAndStartSession();
      }
    }, [sessionInfo]); // Re-run when sessionInfo is updated

    async function startSessionNxtx(avatarData: AvatarData) {
      if (isDesktop) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
      setShowSidebar(false);
      setShowSummarySidebar(false);
      setShowQuizSidebar(false);

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const endDuration = new Date(currentUser?.tenant?.duration?.end_date);
      endDuration.setHours(0, 0, 0, 0);

      if (currentDate > endDuration) {
        setIsLoadingSessionNxtx(false);
        return;
      }

      avatarClickApi.doFetch(
        {
          slug: currentUser ? currentUser?.referral : subDomain,
        },
        {
          onSuccess(data: any) {
            if (currentUser?.tenant?.status === "Trial" && data?.is_hidden) {
              endSessionNxtx();
            }
          },
        }
      );

      try {
        // Validate input data
        if (
          !avatarData.slug ||
          !avatarData.tenant_id ||
          !avatarData.avatar_id ||
          !avatarData.voice_id
        ) {
          throw new Error(
            "Invalid avatar data. Please provide all required fields."
          );
        }

        setAvatarSlug(avatarData.slug.trim());

        setIsLoadingSessionNxtx(true);

        let fetchSessionRes: any;

        if (chatModeTenant) {
          fetchSessionRes = await createSession({ type: "text" });

          trackEventGTM({
            event: "gaia_start_session_a",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: currentUser ? true : false,
              session_id: fetchSessionRes?.data?.session_id,
              screen_state: isScrolled ? "minimize" : "maximize",
              quota: remainingDuration,
            },
          });

          trackEventGA({
            event: "gaia_start_session_a",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: currentUser ? true : false,
              session_id: fetchSessionRes?.data?.session_id,
              screen_state: isScrolled ? "minimize" : "maximize",
              quota: remainingDuration,
            },
          });

          setSessionInfo(fetchSessionRes?.data);

          setSessionId(fetchSessionRes?.data?.session_id);

          setSaveChat((prev) => ({
            ...prev,
            session_id: fetchSessionRes?.data?.session_id,
            start_chat: Math.floor(new Date().getTime() / 1000),
          }));

          initializeWebSocket();
          initializeTaskWebSocket(
            avatarData.slug.trim(),
            fetchSessionRes?.data?.session_id
          );

          setIsFirstAttempt(true);
          setIsRunning(true);
          setStreamNxtx(true);
          setIsLoadingSessionNxtx(false);
        } else {
          fetchSessionRes = await createSession({
            avatar_id: avatarData.avatar_id.trim(),
            voice_id: avatarData.voice_id.trim(),
          });

          if (!fetchSessionRes) {
            setStreamNxtx(false);
            setIsUserTalkingNxtx(false);
            setIsMuted(false);
            setIsPasskeyValid(false);
            setIsRunning(false);
            setIsLoadingSessionNxtx(false);

            if (!isDesktop) {
              const query = { ...router.query };

              delete query.openGaia;

              await router.replace(
                {
                  query,
                },
                undefined,
                { shallow: true }
              );
            }

            showToast({
              type: "danger",
              message: "Failed to get session. Please try again.",
            });
          } else {
            trackEventGTM({
              event: "gaia_start_session_a",
              property: {
                user_id: currentUser?.id ?? "",
                is_logged_in: currentUser ? true : false,
                session_id: fetchSessionRes?.data?.session_id,
                screen_state: isScrolled ? "minimize" : "maximize",
                quota: remainingDuration,
              },
            });

            trackEventGA({
              event: "gaia_start_session_a",
              property: {
                user_id: currentUser?.id ?? "",
                is_logged_in: currentUser ? true : false,
                session_id: fetchSessionRes?.data?.session_id,
                screen_state: isScrolled ? "minimize" : "maximize",
                quota: remainingDuration,
              },
            });

            setSessionInfo(fetchSessionRes?.data);

            setSessionId(fetchSessionRes?.data?.session_id);

            setSaveChat((prev) => ({
              ...prev,
              session_id: fetchSessionRes?.data?.session_id,
              start_chat: Math.floor(new Date().getTime() / 1000),
            }));

            initializeWebSocket();
            initializeTaskWebSocket(
              avatarData.slug.trim(),
              fetchSessionRes?.data?.session_id
            );
          }
        }
      } catch (error) {
        logger.error("Error starting avatar session:", error);
      }
    }

    const toggleMute = () => {
      // Skip microphone operations if in chat mode
      if (chatModeTenant) return;

      if (audioMediaStreamRef.current) {
        try {
          const audioTrack = audioMediaStreamRef.current.getAudioTracks()[0];
          if (audioTrack) {
            const newMuteState = !isMuted;
            audioTrack.enabled = !newMuteState;
            setIsMuted(newMuteState);
            if (newMuteState) {
              vad.pause();

              trackEventGTM({
                event: "gaia_mute_a",
                property: {
                  user_id: currentUser?.id ?? "",
                  is_logged_in: currentUser ? true : false,
                  session_id: sessionInfo?.session_id,
                  screen_state: isScrolled ? "minimize" : "maximize",
                },
              });

              trackEventGA({
                event: "gaia_mute_a",
                property: {
                  user_id: currentUser?.id ?? "",
                  is_logged_in: currentUser ? true : false,
                  session_id: sessionInfo?.session_id,
                  screen_state: isScrolled ? "minimize" : "maximize",
                },
              });
            } else {
              vad.start();

              trackEventGTM({
                event: "gaia_unmute_a",
                property: {
                  user_id: currentUser?.id ?? "",
                  is_logged_in: currentUser ? true : false,
                  session_id: sessionInfo?.session_id,
                  screen_state: isScrolled ? "minimize" : "maximize",
                },
              });

              trackEventGA({
                event: "gaia_unmute_a",
                property: {
                  user_id: currentUser?.id ?? "",
                  is_logged_in: currentUser ? true : false,
                  session_id: sessionInfo?.session_id,
                  screen_state: isScrolled ? "minimize" : "maximize",
                },
              });
            }
          }
        } catch (error) {
          logger.error("Error toggling microphone:", error);
          // Reinitialize microphone if there's an error
          if (!chatModeTenant) {
            initializeMicrophone();
          }
        }
      } else {
        if (!chatModeTenant) {
          initializeMicrophone();
        }
      }
    };

    const initializeMicrophone = async () => {
      // Skip microphone initialization if in chat mode
      if (chatModeTenant) return;

      try {
        const audioMediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true,
          },
        });
        audioMediaStreamRef.current = audioMediaStream;
        setIsMuted(false);
        vad.start();
      } catch (error) {
        logger.error("Error initializing microphone:", error);
        showToast({
          type: "danger",
          message:
            "Unable to access microphone. Please check your permissions.",
        });
      }
    };

    useEffect(() => {
      // Cleanup function to properly release microphone
      return () => {
        if (audioMediaStreamRef.current) {
          audioMediaStreamRef.current
            .getTracks()
            .forEach((track) => track.stop());
          audioMediaStreamRef.current = null;
        }
        vad.pause();
      };
    }, []);

    async function endSessionNxtx() {
      if (isLoadingSessionNxtx) {
        setIsCloseWhenLoading(true);
        return;
      }

      if (!isDesktop) {
        const query = { ...router.query };

        delete query.openGaia;

        await router.replace(
          {
            query,
          },
          undefined,
          { shallow: true }
        );
      }

      trackEventGTM({
        event: "gaia_end_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      trackEventGA({
        event: "gaia_end_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      try {
        localStorage.removeItem("userData");
        localStorage.removeItem("avatarData");

        setSaveChat((prev) => ({
          ...prev,
          end_chat: Math.floor(new Date().getTime() / 1000),
        }));

        setStreamNxtx(false);
        setIsUserTalkingNxtx(false);
        setIsMuted(false);
        setIsRunning(false);
        setIsCloseWhenLoading(false);
        setKeywordChipsData([]);

        if (room) {
          room.disconnect();
          room = null;
        }

        if (analyticSocket.current) {
          analyticSocket.current.close();
          analyticSocket.current = null;
        }

        if (taskSocket.current) {
          taskSocket.current.close();
          taskSocket.current = null;
        }

        // Stop AWS Transcribe
        if (transcribeAbortController.current) {
          transcribeAbortController.current.abort();
          transcribeAbortController.current = null;
        }

        // Stop microphone and release resources
        if (audioMediaStreamRef.current) {
          audioMediaStreamRef.current
            .getTracks()
            .forEach((track) => track.stop());
          audioMediaStreamRef.current = null;
        }

        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }

        // sendEndAction?.();
        // sessionEnd?.();
        closeSession(sessionId);
      } catch (error) {
        logger.error("Error during session termination:", error);
      }
    }

    useEffect(() => {
      const avatarData = getAvatarData();

      if (avatarData) {
        localStorage.removeItem("avatarData");
      }
    }, []);

    useEffect(() => {
      const avatarData = getAvatarData();

      if (avatarData && !hasSessionStarted.current) {
        startSessionNxtx(avatarData);
        hasSessionStarted.current = true; // Prevent further calls
      }
    }, []);

    useEffect(() => {
      let interval: NodeJS.Timeout;

      if (sessionId) {
        interval = setInterval(() => {
          sendCheckpoint({ session_id: sessionId });
        }, 5000);
      }

      return () => {
        clearInterval(interval);
      };
    }, [sessionId]);

    useEffect(() => {
      const avatarData = getAvatarData();

      if (isRunning) {
        sendText(avatarData?.welcome_message, "task_repeat");
      }
    }, [isRunning]);

    useEffect(() => {
      if (isRunning) {
        if (transcribeInitialized.current) return; // Prevent duplicate execution
        transcribeInitialized.current = true; // Mark as initialized

        // Skip transcription setup if in chat mode
        if (chatModeTenant) return;

        transcribeAbortController.current = new AbortController();
        const { signal } = transcribeAbortController.current;

        const avatarData = getAvatarData();

        (async () => {
          try {
            // Only initialize audio if not in chat mode
            if (!chatModeTenant) {
              const audioMediaStream =
                await navigator.mediaDevices.getUserMedia({
                  audio: {
                    noiseSuppression: true, // WebRTC built-in suppression
                    echoCancellation: true, // Helps with room noise
                    autoGainControl: true, // Keeps volume steady
                  },
                });
              audioMediaStreamRef.current = audioMediaStream;

              const audioContext = new AudioContext({ sampleRate: 44100 });
              audioContextRef.current = audioContext;

              const audioWorkletPath =
                "/worklets/audio-chopper-processor.worklet.js";
              // const audioWorkletPath = new URL(
              //   "/src/worklets/audio-chopper-processor.worklet.js",
              //   import.meta.url
              // ).href;
              try {
                await audioContext.audioWorklet.addModule(audioWorkletPath);
                console.log("Worklet loaded successfully");
              } catch (error) {
                console.error("Failed to load worklet:", {
                  error,
                  path: audioWorkletPath,
                  currentDir: import.meta.url,
                });
              }

              const audioStreamSource =
                audioContext.createMediaStreamSource(audioMediaStream);
              const audioRecoderWorklet = new AudioWorkletNode(
                audioContext,
                "audio-chopper-processor.worklet"
              );

              // 🔹 **Noise Reduction Filters**
              // 1️⃣ Low-Pass Filter (removes high-frequency noise)
              const biquadFilter = audioContext.createBiquadFilter();
              biquadFilter.type = "lowpass";
              biquadFilter.frequency.value = 1000; // Removes high-frequency noise

              // 2️⃣ Dynamic Compressor (reduces background noise volume)
              const compressor = audioContext.createDynamicsCompressor();
              compressor.threshold.setValueAtTime(
                -50,
                audioContext.currentTime
              );
              compressor.knee.setValueAtTime(40, audioContext.currentTime);
              compressor.ratio.setValueAtTime(12, audioContext.currentTime);
              compressor.attack.setValueAtTime(0.003, audioContext.currentTime); // Fast response
              compressor.release.setValueAtTime(0.25, audioContext.currentTime); // Smooth decay

              // 🔹 **Connect Processing Nodes**
              audioStreamSource.connect(biquadFilter);
              biquadFilter.connect(compressor);
              compressor.connect(audioRecoderWorklet);
              audioRecoderWorklet.connect(audioContext.destination); // For monitoring (optional)

              // 🔹 **AWS Transcribe Streaming**
              const transcripts = awsTranscribe.startStreamTranscription({
                lang: avatarData.lang_ids,
                sampleRate: audioContext.sampleRate,
                audioStream: async function* () {
                  const audioReadableStream = new ReadableStream<Uint8Array>({
                    start(controller) {
                      audioRecoderWorklet.port.onmessage = (e) => {
                        if (signal.aborted) {
                          controller.close();
                          return;
                        }
                        controller.enqueue(e.data);
                      };
                    },
                  });

                  const reader = audioReadableStream.getReader();

                  try {
                    while (true) {
                      if (signal.aborted) break;

                      const { done, value } = await reader.read();

                      if (done || signal.aborted) {
                        break;
                      }

                      yield { AudioEvent: { AudioChunk: value } };
                    }
                  } finally {
                    reader.releaseLock();
                  }
                },
              });

              for await (const transcript of transcripts) {
                if (signal.aborted) break;
                setTranscript(transcript);
              }
            }
          } catch (error) {
            if (signal.aborted) return;
            logger.error("AWS Transcribe Error:", error);
          }
        })();
      }
    }, [isRunning]);

    useEffect(() => {
      if (streamNxtx) {
        (async () => {
          await sendText(transcript);
        })();
      }
    }, [sessionId, transcript, avatarSlug]);

    useEffect(() => {
      const storedData = getAvatarData();

      if (storedData) {
        setIsPasskeyValid(true);
      }
    }, []);

    useEffect(() => {
      if (streamNxtx && !isLoadingSessionNxtx && isCloseWhenLoading) {
        endSessionNxtx();
      }
    }, [isLoadingSessionNxtx, isCloseWhenLoading, streamNxtx]);

    useEffect(() => {
      endSessionNxtx();
    }, [router.pathname]);

    useEffect(() => {
      if (!router.query.openGaia) {
        endSessionNxtx();
      }
    }, [router.query.openGaia]);

    useEffect(() => {
      return () => {
        endSessionNxtx();
      };
    }, []);

    useEffect(() => {
      if (!currentUser && streamNxtx) {
        endSessionNxtx();
      }
    }, [currentUser]);
    // End NXTX Integration

    const [isDisconnected, setIsDisconnected] = useState(false);

    const avatarClickApi = useGetAvatarClick();
    const getAvatarLogChat = useGetAvatarLogChat();
    const getAvatarLogChatDetail = useGetAvatarLogChatBySessionId();
    const avatarLogChat = useAvatarLogChat();

    useEffect(() => {
      if (player && isPlayerReady) {
        if (streamNxtx || stream) {
          player?.current?.pause();
        } else {
          player?.current?.play();
        }
      }

      if (playerVideoJs?.current) {
        if (streamNxtx || stream) {
          playerVideoJs?.current?.pause();
        } else {
          playerVideoJs?.current?.play();
        }
      }

      if (player?.current) {
        player.current.addEventListener("canPlay", () => {
          console.log("Media Player Ready");
        });
      }

      return () => {
        if (player?.current) {
          player.current.removeEventListener("canPlay", () => {
            console.log("Media Player Not Ready");
          });
        }
      };
    }, [player, media, isPlayerReady, playerVideoJs, streamNxtx, stream]);

    const openAIApiKey = CONST.OPENAI_APIKEY;

    const fetchAccessToken = async () => {
      const trialApiKey = CONST.HEYGEN_APIKEY_TRIAL;
      const apiKey = CONST.HEYGEN_APIKEY;

      const response = await fetch(
        "https://api.heygen.com/v1/streaming.create_token",
        {
          method: "POST",
          headers: {
            "x-api-key": apiKey,
          },
        }
      );

      const { data } = await response.json();
      return data.token;
    };

    let lastDisplayedDate: string | null = null;

    const handleMinimize = (event?: any) => {
      event?.stopPropagation();

      setIsScrolled(true);
    };

    const handleFullScreen = (event) => {
      event.stopPropagation();

      trackEventGTM({
        event: "gaia_maximize_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      trackEventGA({
        event: "gaia_maximize_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      if (window.scrollY === 0) {
        setIsScrolled(false);
      } else {
        const onScroll = () => {
          if (window.scrollY === 0) {
            setIsScrolled(false);

            window.removeEventListener("scroll", onScroll);
          }
        };

        window.addEventListener("scroll", onScroll);

        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    };

    useEffect(() => {
      if (isMyClass) {
        handleMinimize();
      }
    }, [isMyClass]);

    const [activeTab, setActiveTab] = useState("live");

    useEffect(() => {
      if (!chatModeTenant) {
        setChat(() => ({
          chat: [],
        }));
      }
      setSummaryRecap(() => ({
        summary: [],
      }));

      if (currentUser) {
        getAvatarLogChat.doFetch(
          {
            slug: currentUser ? currentUser?.referral : subDomain,
          },
          {
            onSuccess(data: any) {
              let tempChatSet = new Set();

              data?.data?.forEach((item: any) => {
                if (!chatModeTenant) {
                  let parsedChat = JSON.parse(item?.chat_message);

                  parsedChat?.forEach((chat: any) => {
                    tempChatSet.add(
                      JSON.stringify({
                        ...chat,
                        date: item.created_at,
                      })
                    );
                  });
                }

                if (
                  item?.summary_title?.trim() &&
                  item?.summary_content?.trim()
                ) {
                  setSummaryRecap((prev) => ({
                    summary: [
                      ...prev.summary,
                      {
                        session_id: item?.chat_session_id,
                        title: item?.summary_title,
                        content: item?.summary_content,
                        date: item.created_at,
                      },
                    ],
                  }));
                }
              });

              if (!chatModeTenant) {
                const tempChat = Array.from(tempChatSet).map((chat: any) =>
                  JSON.parse(chat)
                );

                setChat(() => ({
                  chat: tempChat,
                }));
              }
            },
          }
        );
      }
    }, [stream, currentUser, streamNxtx, chatModeTenant, newChatRoom]);

    useEffect(() => {
      if (selectedRecapId) {
        setChat(() => ({
          chat: [],
        }));

        getAvatarLogChatDetail.doFetch(
          {
            slug: currentUser ? currentUser?.referral : subDomain,
            sessionId: selectedRecapId,
          },
          {
            onSuccess(data: any) {
              let tempChatSet = new Set();

              let parsedChat = JSON.parse(data?.data?.chat_message);

              parsedChat?.forEach((chat: any) => {
                tempChatSet.add(
                  JSON.stringify({
                    ...chat,
                    date: data?.data?.created_at,
                  })
                );
              });

              const tempChat = Array.from(tempChatSet).map((chat: any) =>
                JSON.parse(chat)
              );

              setChat(() => ({
                chat: tempChat,
              }));
            },
            onError(err) {
              console.log(err);
            },
          }
        );
      }
    }, [selectedRecapId]);

    const [isHiddenAvatar, setIsHiddenAvatar] = useState(false);
    const { remainingDuration, setRemainingDuration } = useAvatarStore();
    const [maxDuration, setMaxDuration] = useState(0);
    const [showLimitDesc, setShowLimitDesc] = useState(false);
    const [isIncreaseLimit, setIsIncreaseLimit] = useState(false);
    const [limitChecked, setLimitChecked] = useState(true);
    const [isRequestSent, setIsRequestSent] = useState(false);

    const formatTime = (seconds) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${String(minutes).padStart(2, "0")}:${String(
        remainingSeconds
      ).padStart(2, "0")}`;
    };

    useEffect(() => {
      if (streamNxtx || stream) {
        if (remainingDuration > 0) {
          const countdown = setInterval(() => {
            setRemainingDuration((prev) => prev - 1);
          }, 1000);

          return () => clearInterval(countdown);
        } else {
          if (nxtxTenant) {
            endSessionNxtx();
          } else {
            endSession();
          }
        }
      }
    }, [remainingDuration, streamNxtx, stream, nxtxTenant]);

    useEffect(() => {
      if (currentUser) {
        avatarClickApi.doFetch(
          {
            slug: currentUser ? currentUser?.referral : subDomain,
          },
          {
            onSuccess(data: any) {
              const duration = data?.max_duration - data?.duration;

              setRemainingDuration(duration <= 0 ? 0 : duration);
              setMaxDuration(data?.max_duration);

              if (duration <= 0) {
                setIsScrolled(true);
              }

              if (currentUser?.tenant?.status === "Trial" && data?.is_hidden) {
                setIsHiddenAvatar(true);

                if (streamNxtx || stream) {
                  if (nxtxTenant) {
                    endSessionNxtx();
                  } else {
                    endSession();
                  }
                }
              }
            },
          }
        );
      }
    }, [currentUser, isSaveChat, streamNxtx, stream, avatar.current]);

    useEffect(() => {
      if (streamNxtx || stream) {
        const interval = setInterval(() => {
          avatarClickApi.doFetch({
            slug: currentUser ? currentUser?.referral : subDomain,
          });
        }, 15000);

        return () => clearInterval(interval);
      }
    }, [currentUser, avatar.current, streamNxtx, stream]);

    const determineAvatarQuality = () => {
      // Get the estimated downlink speed in Mbps
      const downlinkSpeed = (navigator as any).connection?.downlink || 0;

      let quality;

      // Categorize the internet speed
      if (downlinkSpeed < 1) {
        quality = "Low";
      } else if (downlinkSpeed >= 1 && downlinkSpeed < 3) {
        quality = "Medium";
      } else {
        quality = "High";
      }

      // Map the quality to AvatarQuality
      const _AvatarQuality = {
        Low: AvatarQuality.Low,
        Medium: AvatarQuality.Medium,
        High: AvatarQuality.High,
      };

      return _AvatarQuality[quality];
    };

    async function startSession() {
      if (isDesktop) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
      setShowSidebar(false);
      setShowSummarySidebar(false);
      setShowQuizSidebar(false);

      trackEventGTM({
        event: "gaia_start_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
          quota: remainingDuration,
        },
      });

      trackEventGA({
        event: "gaia_start_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
          quota: remainingDuration,
        },
      });

      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      const endDuration = new Date(currentUser?.tenant?.duration?.end_date);
      endDuration.setHours(0, 0, 0, 0);

      if (currentDate > endDuration) {
        setIsLoadingSession(false);
        return;
      }

      avatarClickApi.doFetch(
        {
          slug: currentUser ? currentUser?.referral : subDomain,
        },
        {
          onSuccess(data: any) {
            if (currentUser?.tenant?.status === "Trial" && data?.is_hidden) {
              endSession();
            }
          },
        }
      );

      setIsLoadingSession(true);
      const newToken = await fetchAccessToken();
      const quality = determineAvatarQuality();

      avatar.current = new StreamingAvatar({
        token: newToken,
      });
      avatar.current.on(StreamingEvents.AVATAR_START_TALKING, (e) => {
        setDisabledMic(false);
        console.log("Avatar started talking", e);
      });
      avatar.current.on(StreamingEvents.AVATAR_TALKING_MESSAGE, (message) => {
        setAvatarMessage((prev) => [...prev, message?.detail?.message]);
        console.log("Avatar talking message:", message?.detail?.message);
      });
      avatar.current.on(StreamingEvents.AVATAR_END_MESSAGE, (message) => {
        setAvatarEndMessage(true);
        console.log("Avatar end message:", message);
      });
      avatar.current.on(StreamingEvents.AVATAR_STOP_TALKING, (e) => {
        console.log("Avatar stopped talking", e);
      });
      avatar.current.on(StreamingEvents.STREAM_DISCONNECTED, () => {
        setSaveChat((prev) => ({
          ...prev,
          end_chat: Math.floor(new Date().getTime() / 1000),
        }));
        setAvatarMessage([]);
        setUserMessage([]);
        console.log("Stream disconnected");

        setIsDisconnected(true);
        setIsLoadingSession(false);
        endSession();
      });
      avatar.current?.on(StreamingEvents.STREAM_READY, (event) => {
        setSaveChat((prev) => ({
          ...prev,
          start_chat: Math.floor(new Date().getTime() / 1000),
        }));
        console.log("Stream ready:", event.detail);
        setIsLoadingSession(false);
        setStream(event.detail);
        setStreamAvatar(event.detail);
        setIsDisconnected(false);
      });
      avatar.current?.on(StreamingEvents.USER_START, (event) => {
        setDisabledMic(true);
        console.log("User started talking:", event);
        setIsUserTalking(true);
      });
      avatar.current.on(StreamingEvents.USER_TALKING_MESSAGE, (message) => {
        setUserMessage((prev) => [...prev, message?.detail?.message]);
        console.log("User talking message:", message);
      });
      avatar.current.on(StreamingEvents.USER_END_MESSAGE, (message) => {
        setUserEndMessage(true);
        console.log("User end message:", message);
      });
      avatar.current?.on(StreamingEvents.USER_STOP, (event) => {
        console.log("User stopped talking:", event);
        setIsUserTalking(false);
      });
      try {
        const res = await avatar.current.createStartAvatar({
          quality: quality,
          avatarName: currentUser?.tenant?.setting?.heygen_avatar,
          voice: {
            voiceId: currentUser?.tenant?.setting?.heygen_voice,
            rate: 1.0,
            emotion: VoiceEmotion.EXCITED,
          },
          knowledgeId: currentUser?.tenant?.setting?.heygen_knowledge,
          language: currentUser?.tenant?.setting?.heygen_language,
          disableIdleTimeout: false, // if false, end session when user is not talking
        });

        setAvatar(avatar.current);

        INTRO.map((item) => {
          if (
            item?.knowledgeId === currentUser?.tenant?.setting?.heygen_knowledge
          ) {
            if (currentUser?.tenant?.setting?.heygen_language === "en") {
              setText(item?.introTextEN);
            } else {
              setText(item?.introTextID);
            }
          }
        });

        setDataAvatar(res);
        setDataAvatarGlobal(res);
        setSaveChat((prev) => ({
          ...prev,
          session_id: res?.session_id,
          start_chat: Math.floor(new Date().getTime() / 1000),
        }));
        await avatar.current?.startVoiceChat({
          useSilencePrompt: false,
        });
        setChatMode("voice_mode");
        setIsVoiceChatActive(true);
      } catch (error) {
        setIsLoadingSession(false);

        console.error("Error starting avatar session:", error);
      }
    }

    const {
      isCourseTranscript,
      courseTranscript,
      setIsCourseTranscript,
      setCourseTranscript,
    } = useAvatarStore();

    useEffect(() => {
      const handleCourseTranscript = async () => {
        if (!isCourseTranscript) return;

        if (remainingDuration > 0) {
          if (nxtxTenant) {
            await handlePasskeySubmit();
          } else {
            startSession();
          }
        } else {
          trackEventGTM({
            event: "gaia_quota_s",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: !!currentUser,
              session_id: sessionInfo?.session_id,
              screen_state: isScrolled ? "minimize" : "maximize",
            },
          });

          trackEventGA({
            event: "gaia_quota_s",
            property: {
              user_id: currentUser?.id ?? "",
              is_logged_in: !!currentUser,
              session_id: sessionInfo?.session_id,
              screen_state: isScrolled ? "minimize" : "maximize",
            },
          });
        }
      };

      handleCourseTranscript();
    }, [isCourseTranscript]);

    useEffect(() => {
      const fetchData = async () => {
        await handleSpeak();
      };

      fetchData();
      setIsIntro(true);
    }, [text]);

    // useEffect(() => {
    //   const fetchData = async () => {
    //     await handleSpeakQuiz();
    //     await handleInterrupt();
    //   };

    //   fetchData();
    // }, [textQuiz, isIntro]);

    const API_URL = "https://api.openai.com/v1/chat/completions";

    function getTextsAfterColon(input) {
      const lines = input.split("\n");
      return lines.map((line) => {
        const colonIndex = line.indexOf(":");
        return colonIndex !== -1 ? line.substring(colonIndex + 1).trim() : null;
      });
    }

    const [keywordChipsData, setKeywordChipsData] = useState<any>([]);

    const getKeywordChipsData = async (sentences: any) => {
      const promptText = `Buatkan 3 FAQ singkat (Tidak lebih dari 3 kata) atau keyword untuk memudahkan user bertanya terhadap suatu topik dari penjelasan berikut : "${JSON.stringify(
        sentences
      )}"`;

      const messages = [{ role: "user", content: promptText }];

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${CONST.OPENAI_APIKEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-3.5-turbo",
            messages: messages,
            stream: false,
          }),
        });

        const responseJson = await response.json();

        return responseJson.choices[0].message.content;
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      if (avatarMessageNxtx) {
        getKeywordChipsData(avatarMessageNxtx).then((res) => {
          setKeywordChipsData(res.split("\n").map((item) => item.trim()));
        });
      }
    }, [avatarMessageNxtx]);

    useEffect(() => {
      const isQuizPrompt = userMessage.toString().includes("PERSONA");

      if (isIntro && avatarEndMessage) {
        setSaveChat((prev) => ({
          ...prev,
          chat: [...prev.chat, { user: "-", avatar: avatarMessage.join("") }],
        }));

        setChat((prev) => ({
          chat: [
            ...prev.chat,
            {
              user: "-",
              avatar: avatarMessage.join(""),
              date: new Date().toISOString(),
            },
          ],
        }));

        setAvatarMessage([]);

        setAvatarEndMessage(false);
        setIsIntro(false);
      }

      if (avatarEndMessage && userEndMessage) {
        setSaveChat((prev) => ({
          ...prev,
          chat: [
            ...prev.chat,
            {
              user: !isQuizPrompt ? userMessage.toString() : "-",
              avatar: avatarMessage.join(""),
            },
          ],
        }));

        setChat((prev) => ({
          chat: [
            ...prev.chat,
            {
              user: !isQuizPrompt ? userMessage.toString() : "-",
              avatar: avatarMessage.join(""),
              date: new Date().toISOString(),
            },
          ],
        }));

        setAvatarMessage([]);
        setUserMessage([]);

        setAvatarEndMessage(false);
        setUserEndMessage(false);
      }
    }, [avatarEndMessage, userEndMessage, isIntro]);

    useEffect(() => {
      const data = {
        slug: currentUser ? currentUser?.referral : subDomain,
        ...saveChat,
      };

      setIsSaveChat(false);

      if (isDisconnected || !streamNxtx) {
        avatarLogChat.doFetch(data, {
          onSuccess(data: any) {
            if (currentUser?.tenant?.status === "Trial" && data?.is_hidden) {
              setIsHiddenAvatar(true);
            }

            setIsDisconnected(false);
            setIsSaveChat(true);

            setSaveChat((prev) => ({
              ...prev,
              chat: [],
            }));
          },
          onError(err) {
            const error: any = err.getActualType();
            console.error("Error saving avatar log chat:", error);

            setIsDisconnected(false);
          },
        });
      }
    }, [isDisconnected, currentUser, streamNxtx]);

    const createChatRoom = async () => {
      const newSessionId = generateUUIDv1();

      setSaveChat((prev) => ({
        ...prev,
        session_id: newSessionId,
      }));

      const data = {
        slug: currentUser ? currentUser?.referral : subDomain,
        ...saveChat,
      };

      setIsNewChatRoomLoading(true);

      avatarLogChat.doFetch(data, {
        onSuccess(data: any) {
          setSaveChat((prev) => ({
            ...prev,
            chat: [],
          }));

          setNewChatRoom(false);
          setSelectedRecapId(data?.session_id);
          setIsReadyToFetch(false);

          setIsNewChatRoomLoading(false);
        },
        onError(err) {
          const error: any = err.getActualType();
          console.error("Error saving avatar log chat:", error);

          setIsReadyToFetch(false);

          setIsNewChatRoomLoading(false);
        },
      });
    };

    async function handleStartVoiceChat(event) {
      event.stopPropagation();

      trackEventGTM({
        event: "gaia_unmute_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      trackEventGA({
        event: "gaia_unmute_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      setMicIsLoading(true);
      if (!avatar.current) {
        setDebug("Avatar API not initialized");
        return;
      }

      await avatar.current?.startVoiceChat();
      setMicIsLoading(false);
      setIsVoiceChatActive(true);
    }

    function handleCloseVoiceChat(event) {
      event.stopPropagation();

      trackEventGTM({
        event: "gaia_mute_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      trackEventGA({
        event: "gaia_mute_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      setMicIsLoading(true);
      if (!avatar.current) {
        setDebug("Avatar API not initialized");
        return;
      }

      avatar.current?.closeVoiceChat();
      setMicIsLoading(false);
      setIsVoiceChatActive(false);
    }

    async function handleSpeak() {
      setIsLoadingRepeat(true);
      if (!avatar.current) {
        setDebug("Avatar API not initialized");

        return;
      }
      await avatar.current
        .speak({
          text: isCourseTranscript ? courseTranscript : text,
          taskType: isCourseTranscript ? TaskType.TALK : TaskType.REPEAT,
          taskMode: TaskMode.SYNC,
        })
        .catch((e) => {
          setDebug(e.message);
        });
      setIsLoadingRepeat(false);
    }

    async function handleSpeakText(manualText?: string) {
      setIsLoadingRepeat(true);
      if (!avatar.current) {
        setDebug("Avatar API not initialized");

        return;
      }
      await avatar.current
        .speak({
          text: manualText ?? textValue,
          taskType: TaskType.TALK,
          taskMode: TaskMode.SYNC,
        })
        .catch((e) => {
          setDebug(e.message);
        });
      setIsLoadingRepeat(false);
    }

    async function handleSpeakQuiz() {
      setIsLoadingRepeat(true);
      if (!avatar.current) {
        setDebug("Avatar API not initialized");

        return;
      }
      await avatar.current
        .speak({
          text: textQuiz,
          taskType: TaskType.TALK,
          taskMode: TaskMode.SYNC,
        })
        .catch((e) => {
          setDebug(e.message);
        });
      setIsLoadingRepeat(false);
    }

    async function handleInterrupt() {
      if (!avatar.current) {
        setDebug("Avatar API not initialized");

        return;
      }
      await avatar.current.interrupt().catch((e) => {
        setDebug(e.message);
      });
    }

    useImperativeHandle(ref, () => ({
      handleInterrupt: nxtxTenant ? sendInterrupt : handleInterrupt,
    }));

    async function endSession() {
      if (isLoadingSession) {
        setIsCloseWhenLoading(true);
        return;
      }

      if (!isDesktop) {
        const query = { ...router.query };

        delete query.openGaia;

        await router.replace(
          {
            query,
          },
          undefined,
          { shallow: true }
        );
      }

      setIsCourseTranscript(false);
      setCourseTranscript("");

      if (!avatar.current) {
        setDebug("Avatar API not initialized");
        return;
      }

      trackEventGTM({
        event: "gaia_end_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      trackEventGA({
        event: "gaia_end_session_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: dataAvatar?.session_id,
          screen_state: isScrolled ? "minimize" : "maximize",
        },
      });

      avatar.current.closeVoiceChat();
      await avatar.current.stopAvatar();
      setText("");
      setStream(undefined);
      setStreamAvatar(undefined);
      setIsVoiceChatActive(false);
      setIsCloseWhenLoading(false);
    }

    const handleChangeChatMode = useMemoizedFn(async (v) => {
      if (v === chatMode) {
        return;
      }

      setModeIsLoading(true);
      if (v === "text_mode") {
        avatar.current?.closeVoiceChat();
        trackEventGTM({
          event: "gaia_text_a",
          property: {
            user_id: currentUser?.id ?? "",
          },
        });

        trackEventGA({
          event: "gaia_text_a",
          property: {
            user_id: currentUser?.id ?? "",
          },
        });
      } else {
        await avatar.current?.startVoiceChat();
        trackEventGTM({
          event: "gaia_mic_a",
          property: {
            user_id: currentUser?.id ?? "",
          },
        });

        trackEventGA({
          event: "gaia_mic_a",
          property: {
            user_id: currentUser?.id ?? "",
          },
        });
      }
      setModeIsLoading(false);

      setChatMode(v);
    });

    useEffect(() => {
      if (stream && !isLoadingSession && isCloseWhenLoading) {
        endSession();
      }
    }, [isLoadingSession, isCloseWhenLoading, stream]);

    useEffect(() => {
      endSession();
    }, [router.pathname]);

    useEffect(() => {
      if (!router.query.openGaia) {
        endSession();
      }
    }, [router.query.openGaia]);

    useEffect(() => {
      if (isScrolled) {
        handleChangeChatMode("voice_mode");
      }
    }, [isScrolled]);

    const previousText = usePrevious(textValue);
    useEffect(() => {
      if (!previousText && textValue) {
        avatar.current?.startListening();
      } else if (previousText && !textValue) {
        avatar?.current?.stopListening();
      }
    }, [textValue, previousText]);

    useEffect(() => {
      return () => {
        endSession();
      };
    }, []);

    const [isMediaReady, setIsMediaReady] = useState(false);

    const isMalukuTenant =
      typeof window !== "undefined" && isMalukuURL(window.location.hostname);

    const tenantsAvatar = ["maluku-gate", "asuransiastra"];

    const initializeMediaStream = () => {
      if (mediaStream.current) {
        const previousScroll = window.scrollY;

        setDisabledMic(true);
        mediaStream.current.srcObject = null;
        mediaStream.current.src = `/images/${
          (tenantData as any)?.setting?.heygen_avatar
        }.mp4`;
        mediaStream.current.muted = true;
        mediaStream.current.loop = true;

        mediaStream.current.onloadedmetadata = () => {
          mediaStream.current!.play();
          window.scrollTo(0, previousScroll);
        };
      }
    };

    useEffect(() => {
      if (isMediaReady) {
        if (stream && mediaStream.current) {
          const previousScroll = window.scrollY;

          setDisabledMic(false);
          mediaStream.current.srcObject = stream;
          mediaStream.current.muted = false;
          mediaStream.current.loop = false;

          mediaStream.current.onloadedmetadata = () => {
            mediaStream.current!.play();
            window.scrollTo(0, previousScroll);
            setDebug("Playing");
          };
        } else {
          initializeMediaStream();
        }
      }
    }, [stream, isMediaReady]);

    const stopMediaTracks = (stream: MediaStream) => {
      stream.getTracks().forEach((track) => {
        if (track.readyState === "live" && track.kind === "audio") {
          track.stop(); // Hentikan track audio
        }
      });
    };

    useEffect(() => {
      return () => {
        // Pastikan stream audio dihentikan ketika komponen unmounted
        if (mediaRecorderRef.current) {
          const stream = mediaRecorderRef.current.stream;
          stopMediaTracks(stream);
        }
      };
    }, []);

    const [showAllSidebar, setShowAllSidebar] = useState(false);
    const {
      showSidebar,
      setShowSidebar,
      showSummarySidebar,
      setShowSummarySidebar,
      showQuizSidebar,
      setShowQuizSidebar,
    } = useAvatarStore();

    const toggleSidebar = (event) => {
      event.stopPropagation();

      trackEventGTM({
        event: showSidebar ? "gaia_transcript_close_a" : "gaia_transcript_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      trackEventGA({
        event: showSidebar ? "gaia_transcript_close_a" : "gaia_transcript_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      setShowSidebar(!showSidebar);
      setShowSummarySidebar(false);
      setShowQuizSidebar(false);
      setShowAllSidebar(false);
    };

    const toggleSummarySidebar = (event) => {
      event.stopPropagation();

      setShowSummarySidebar(!showSummarySidebar);
      setShowSidebar(false);
      setShowQuizSidebar(false);
      setShowAllSidebar(false);
    };

    const toggleQuizSidebar = (event) => {
      event.stopPropagation();

      setShowQuizSidebar(!showQuizSidebar);
      setShowSidebar(false);
      setShowSummarySidebar(false);
      setShowAllSidebar(false);
    };

    const [copySuccess, setCopySuccess] = useState({});

    const handleCopyText = (event, textToCopy, index) => {
      event.stopPropagation();

      trackEventGTM({
        event: "gaia_transcript_copy_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      trackEventGA({
        event: "gaia_transcript_copy_a",
        property: {
          user_id: currentUser?.id ?? "",
          is_logged_in: currentUser ? true : false,
          session_id: sessionInfo?.session_id,
        },
      });

      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          setCopySuccess((prevState) => ({
            ...prevState,
            [index]: true,
          }));

          setTimeout(() => {
            setCopySuccess((prevState) => ({
              ...prevState,
              [index]: false,
            }));
          }, 2000);
        })
        .catch(() => {
          setCopySuccess((prevState) => ({
            ...prevState,
            [index]: false,
          }));
        });
    };

    const scrollToTop = (event) => {
      event.stopPropagation();

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    const handleBigger = (event) => {
      event.stopPropagation();

      setIsSmaller(false);
    };

    const handleSmaller = (event) => {
      event.stopPropagation();

      setIsSmaller(true);
    };

    useEffect(() => {
      setTimeout(() => {
        setCopySuccess(false);
      }, 3000);
    }, [copySuccess]);

    const [volumes, setVolumes] = useState<number[]>(Array(10).fill(0));
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);

    useEffect(() => {
      const handleAudio = async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          setAudioStream(stream);

          const audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();

          const analyser = audioContext.createAnalyser();
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
          analyser.fftSize = 256;

          const dataArray = new Uint8Array(analyser.frequencyBinCount);

          const updateVolume = () => {
            analyser.getByteFrequencyData(dataArray);

            if (isVoiceChatActive) {
              const newVolumes = Array.from(
                { length: 10 },
                (_, i) => dataArray[i * 2] / 2
              );
              setVolumes(newVolumes);
            }

            requestAnimationFrame(updateVolume);
          };

          updateVolume();
        } catch (err) {
          console.error("Error accessing microphone:", err);
        }
      };

      handleAudio();

      return () => {
        if (audioStream) {
          audioStream.getTracks().forEach((track) => track.stop());
        }
      };
    }, [isVoiceChatActive]);

    useEffect(() => {
      if (!isVoiceChatActive && audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
    }, [isVoiceChatActive, audioStream]);

    const chatEndRef = useRef(null);
    const summaryEndRef = useRef(null);
    const summaryRecapRef = useRef(null);

    useEffect(() => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [chat.chat, isScrolled, showSidebar]);

    useEffect(() => {
      if (summaryEndRef.current) {
        summaryEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [isScrolled, showSummarySidebar, activeTab]);

    useEffect(() => {
      if (summaryRecapRef.current) {
        summaryRecapRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [summaryRecap.summary, isScrolled, showSummarySidebar, activeTab]);

    const requestQuota = useRequestAvatarQuota();

    const handleRequestQuota = async (e) => {
      e.stopPropagation();

      requestQuota.doFetch(
        {
          slug: currentUser?.referral,
        },
        {
          onSuccess: (data) => {
            setIsRequestSent(true);
            showToast({
              type: "info",
              message: "Request Terkirim",
            });
          },
        }
      );
    };

    useEffect(() => {
      if (currentUser?.has_request_quota_gaia) {
        setIsRequestSent(true);
      }
    }, [currentUser]);

    // @ts-ignore
    const getSubdomainAPI = useGetSubdomainData();
    const { tenantData, setTenantData } = useAuthStore();

    useEffect(() => {
      getSubdomainAPI.doFetch(
        {
          slug:
            window.location.hostname === "e-learning.bluebirdgroup.com"
              ? "bluebird"
              : window.location.hostname === "lms.injourney.id"
              ? "injourney"
              : currentUser?.referral ?? subDomain,
        },
        {
          onSuccess(data: any) {
            setTenantData(data?.data);
          },
        }
      );
    }, [currentUser, subDomain]);

    const [isHeygenEnabled, setIsHeygenEnabled] = useState(false);

    useEffect(() => {
      if ((tenantData as any)?.setting?.is_heygen_enabled) {
        setIsHeygenEnabled(true);
      } else {
        setIsHeygenEnabled(false);
      }
    }, [CONST, tenantData]);

    useEffect(() => {
      if (isHeygenEnabled) {
        trackEventGTM({
          event: "gaia_homepage_s",
          property: {
            user_id: currentUser?.id ?? "",
            is_logged_in: currentUser ? true : false,
          },
        });

        trackEventGA({
          event: "gaia_homepage_s",
          property: {
            user_id: currentUser?.id ?? "",
            is_logged_in: currentUser ? true : false,
          },
        });
      }
    }, [isHeygenEnabled, currentUser]);

    useEffect(() => {
      if (!currentUser && stream) {
        endSession();
      }
    }, [currentUser]);

    useEffect(() => {
      const handleGaiaQuery = async () => {
        if (router.query.openGaia === "true") {
          if (remainingDuration > 0) {
            if (nxtxTenant) {
              await handlePasskeySubmit();
            } else {
              startSession();
            }
          } else {
            if (!isDesktop) {
              const query = { ...router.query };
              delete query.openGaia;
              await router.replace(
                {
                  query,
                },
                undefined,
                { shallow: true }
              );
            }

            trackEventGTM({
              event: "gaia_quota_s",
              property: {
                user_id: currentUser?.id ?? "",
                is_logged_in: currentUser ? true : false,
                session_id: sessionInfo?.session_id,
                screen_state: isScrolled ? "minimize" : "maximize",
              },
            });

            trackEventGA({
              event: "gaia_quota_s",
              property: {
                user_id: currentUser?.id ?? "",
                is_logged_in: currentUser ? true : false,
                session_id: sessionInfo?.session_id,
                screen_state: isScrolled ? "minimize" : "maximize",
              },
            });
          }
        }
      };

      handleGaiaQuery();
    }, [router.query]);

    const gaiaAccess = useAccessGaiaTenant();
    const [isGaiaAccess, setIsGaiaAccess] = useState(true);

    useEffect(() => {
      if (currentUser) {
        gaiaAccess.doFetch(
          {
            slug: currentUser?.referral ?? subDomain,
          },
          {
            onSuccess(data: any) {
              setIsGaiaAccess(checkGaiaAccess(data?.data, currentUser));
            },
          }
        );
      }
    }, [currentUser]);

    function checkGaiaAccess(accessData, user) {
      const groupedAccess = {
        positions: [],
        workUnits: [],
        locations: [],
      };

      let isUserAccess = false;

      accessData.forEach((access) => {
        const accessType = access.access_type.name;
        const isExactUser = access.is_exact_user;
        const isOpenToEveryone = access.is_open_to_everyone;

        if (accessType === "User" && (isExactUser || isOpenToEveryone)) {
          isUserAccess = true;
        }

        const typeData = access.type_json_data || {};

        if (typeData.positions) {
          groupedAccess.positions.push(...typeData.positions);
        }

        if (typeData.work_units) {
          groupedAccess.workUnits.push(...typeData.work_units);
        }

        if (typeData.locations) {
          groupedAccess.locations.push(...typeData.locations);
        }
      });

      const allowedPositions = groupedAccess.positions
        .map((p) => p?.position)
        .filter(Boolean);
      const allowedWorkUnits = groupedAccess.workUnits
        .map((wu) => wu?.work_unit)
        .filter(Boolean);
      const allowedLocations = groupedAccess.locations
        .map((loc) => loc?.location)
        .filter(Boolean);

      const hasPositionAccess = allowedPositions.includes(
        user?.profile?.position
      );
      const hasWorkUnitAccess = allowedWorkUnits.includes(
        user?.profile?.work_unit
      );
      const hasLocationAccess = allowedLocations.includes(
        user?.profile?.location
      );

      return (
        isUserAccess ||
        hasPositionAccess ||
        hasWorkUnitAccess ||
        hasLocationAccess
      );
    }

    return (
      <>
        {!isDesktop &&
          isHeygenEnabled &&
          !isCourse &&
          isGaiaAccess &&
          currentUser && (
            <>
              {chatModeTenant ? (
                <AvatarMobileChat
                  isReadyToFetch={isReadyToFetch}
                  setIsReadyToFetch={setIsReadyToFetch}
                  isNewChatRoomLoading={isNewChatRoomLoading}
                  setIsNewChatRoomLoading={setIsNewChatRoomLoading}
                  createChatRoom={createChatRoom}
                  newChatRoom={newChatRoom}
                  setNewChatRoom={setNewChatRoom}
                  selectedRecapId={selectedRecapId}
                  setSelectedRecapId={setSelectedRecapId}
                  chatModeTenant={chatModeTenant}
                  nxtxTenant={nxtxTenant}
                  sendInterrupt={sendInterrupt}
                  sendText={sendText}
                  VisualAudioContext={VisualAudioContext}
                  handlePasskeySubmit={handlePasskeySubmit}
                  keywordChipsData={keywordChipsData}
                  isCloseWhenLoading={isCloseWhenLoading}
                  setIsCloseWhenLoading={setIsCloseWhenLoading}
                  isVisible={isVisible}
                  handleInterrupt={nxtxTenant ? sendInterrupt : handleInterrupt}
                  dataAvatar={nxtxTenant ? sessionInfo : dataAvatar}
                  isAnyFiltered={isAnyFiltered}
                  isKeywordsSearched={isKeywordsSearched}
                  isScrolled={isScrolled}
                  handleMinimize={handleMinimize}
                  isSmaller={isSmaller}
                  mediaStream={nxtxTenant ? mediaElementRef : mediaStream}
                  isMediaReady={isMediaReady}
                  setIsMediaReady={setIsMediaReady}
                  startSession={nxtxTenant ? startSessionNxtx : startSession}
                  endSession={nxtxTenant ? endSessionNxtx : endSession}
                  isLoadingSession={
                    nxtxTenant ? isLoadingSessionNxtx : isLoadingSession
                  }
                  stream={nxtxTenant ? streamNxtx : stream}
                  toggleSidebar={toggleSidebar}
                  showSidebar={showSidebar}
                  toggleSummarySidebar={toggleSummarySidebar}
                  showSummarySidebar={showSummarySidebar}
                  toggleQuizSidebar={toggleQuizSidebar}
                  showQuizSidebar={showQuizSidebar}
                  chat={chat}
                  lastDisplayedDate={lastDisplayedDate}
                  currentUser={currentUser}
                  handleCopyText={handleCopyText}
                  copySuccess={copySuccess}
                  chatEndRef={chatEndRef}
                  summaryRecap={summaryRecap}
                  summaryEndRef={summaryEndRef}
                  scrollToTop={scrollToTop}
                  handleBigger={handleBigger}
                  handleSmaller={handleSmaller}
                  volumes={volumes}
                  isVoiceChatActive={isVoiceChatActive}
                  isMuted={isMuted}
                  handleCloseVoiceChat={
                    nxtxTenant ? toggleMute : handleCloseVoiceChat
                  }
                  handleStartVoiceChat={
                    nxtxTenant ? toggleMute : handleStartVoiceChat
                  }
                  disabledMic={disabledMic}
                  micIsLoading={micIsLoading}
                  modeIsLoading={modeIsLoading}
                  remainingDuration={remainingDuration}
                  maxDuration={maxDuration}
                  formatTime={formatTime}
                  showLimitDesc={showLimitDesc}
                  setShowLimitDesc={setShowLimitDesc}
                  isRequestSent={isRequestSent}
                  setIsRequestSent={setIsRequestSent}
                  isIncreaseLimit={isIncreaseLimit}
                  setIsIncreaseLimit={setIsIncreaseLimit}
                  limitChecked={limitChecked}
                  setLimitChecked={setLimitChecked}
                  showAllSidebar={showAllSidebar}
                  setShowAllSidebar={setShowAllSidebar}
                  handleFullScreen={handleFullScreen}
                  showToast={showToast}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleRequestQuota={handleRequestQuota}
                  chatMode={chatMode}
                  isUserTalking={nxtxTenant ? isUserTalkingNxtx : isUserTalking}
                  textValue={textValue}
                  setTextValue={setTextValue}
                  handleSpeakText={handleSpeakText}
                  isLoadingRepeat={isLoadingRepeat}
                  handleChangeChatMode={handleChangeChatMode}
                  saveChat={saveChat}
                  textQuiz={textQuiz}
                  setTextQuiz={setTextQuiz}
                  handleSpeakQuiz={handleSpeakQuiz}
                />
              ) : (
                <AvatarMobile
                  chatModeTenant={chatModeTenant}
                  nxtxTenant={nxtxTenant}
                  sendInterrupt={sendInterrupt}
                  sendText={sendText}
                  VisualAudioContext={VisualAudioContext}
                  handlePasskeySubmit={handlePasskeySubmit}
                  keywordChipsData={keywordChipsData}
                  isCloseWhenLoading={isCloseWhenLoading}
                  setIsCloseWhenLoading={setIsCloseWhenLoading}
                  isVisible={isVisible}
                  handleInterrupt={nxtxTenant ? sendInterrupt : handleInterrupt}
                  dataAvatar={nxtxTenant ? sessionInfo : dataAvatar}
                  isAnyFiltered={isAnyFiltered}
                  isKeywordsSearched={isKeywordsSearched}
                  isScrolled={isScrolled}
                  handleMinimize={handleMinimize}
                  isSmaller={isSmaller}
                  mediaStream={nxtxTenant ? mediaElementRef : mediaStream}
                  isMediaReady={isMediaReady}
                  setIsMediaReady={setIsMediaReady}
                  startSession={nxtxTenant ? startSessionNxtx : startSession}
                  endSession={nxtxTenant ? endSessionNxtx : endSession}
                  isLoadingSession={
                    nxtxTenant ? isLoadingSessionNxtx : isLoadingSession
                  }
                  stream={nxtxTenant ? streamNxtx : stream}
                  toggleSidebar={toggleSidebar}
                  showSidebar={showSidebar}
                  toggleSummarySidebar={toggleSummarySidebar}
                  showSummarySidebar={showSummarySidebar}
                  toggleQuizSidebar={toggleQuizSidebar}
                  showQuizSidebar={showQuizSidebar}
                  chat={chat}
                  lastDisplayedDate={lastDisplayedDate}
                  currentUser={currentUser}
                  handleCopyText={handleCopyText}
                  copySuccess={copySuccess}
                  chatEndRef={chatEndRef}
                  summaryRecap={summaryRecap}
                  summaryEndRef={summaryEndRef}
                  scrollToTop={scrollToTop}
                  handleBigger={handleBigger}
                  handleSmaller={handleSmaller}
                  volumes={volumes}
                  isVoiceChatActive={isVoiceChatActive}
                  isMuted={isMuted}
                  handleCloseVoiceChat={
                    nxtxTenant ? toggleMute : handleCloseVoiceChat
                  }
                  handleStartVoiceChat={
                    nxtxTenant ? toggleMute : handleStartVoiceChat
                  }
                  disabledMic={disabledMic}
                  micIsLoading={micIsLoading}
                  modeIsLoading={modeIsLoading}
                  remainingDuration={remainingDuration}
                  maxDuration={maxDuration}
                  formatTime={formatTime}
                  showLimitDesc={showLimitDesc}
                  setShowLimitDesc={setShowLimitDesc}
                  isRequestSent={isRequestSent}
                  setIsRequestSent={setIsRequestSent}
                  isIncreaseLimit={isIncreaseLimit}
                  setIsIncreaseLimit={setIsIncreaseLimit}
                  limitChecked={limitChecked}
                  setLimitChecked={setLimitChecked}
                  showAllSidebar={showAllSidebar}
                  setShowAllSidebar={setShowAllSidebar}
                  handleFullScreen={handleFullScreen}
                  showToast={showToast}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleRequestQuota={handleRequestQuota}
                  chatMode={chatMode}
                  isUserTalking={nxtxTenant ? isUserTalkingNxtx : isUserTalking}
                  textValue={textValue}
                  setTextValue={setTextValue}
                  handleSpeakText={handleSpeakText}
                  isLoadingRepeat={isLoadingRepeat}
                  handleChangeChatMode={handleChangeChatMode}
                  saveChat={saveChat}
                  textQuiz={textQuiz}
                  setTextQuiz={setTextQuiz}
                  handleSpeakQuiz={handleSpeakQuiz}
                />
              )}
            </>
          )}

        {isDesktop &&
          isHeygenEnabled &&
          !isCourse &&
          isGaiaAccess &&
          currentUser && (
            <>
              {chatModeTenant ? (
                <AvatarDesktopChat
                  isReadyToFetch={isReadyToFetch}
                  setIsReadyToFetch={setIsReadyToFetch}
                  isNewChatRoomLoading={isNewChatRoomLoading}
                  setIsNewChatRoomLoading={setIsNewChatRoomLoading}
                  createChatRoom={createChatRoom}
                  newChatRoom={newChatRoom}
                  setNewChatRoom={setNewChatRoom}
                  selectedRecapId={selectedRecapId}
                  setSelectedRecapId={setSelectedRecapId}
                  chatModeTenant={chatModeTenant}
                  nxtxTenant={nxtxTenant}
                  sendInterrupt={sendInterrupt}
                  sendText={sendText}
                  VisualAudioContext={VisualAudioContext}
                  handlePasskeySubmit={handlePasskeySubmit}
                  keywordChipsData={keywordChipsData}
                  isCloseWhenLoading={isCloseWhenLoading}
                  setIsCloseWhenLoading={setIsCloseWhenLoading}
                  isVisible={isVisible}
                  handleInterrupt={nxtxTenant ? sendInterrupt : handleInterrupt}
                  dataAvatar={nxtxTenant ? sessionInfo : dataAvatar}
                  isAnyFiltered={isAnyFiltered}
                  isKeywordsSearched={isKeywordsSearched}
                  isScrolled={isScrolled}
                  handleMinimize={handleMinimize}
                  isSmaller={isSmaller}
                  mediaStream={nxtxTenant ? mediaElementRef : mediaStream}
                  isMediaReady={isMediaReady}
                  setIsMediaReady={setIsMediaReady}
                  startSession={nxtxTenant ? startSessionNxtx : startSession}
                  endSession={nxtxTenant ? endSessionNxtx : endSession}
                  isLoadingSession={
                    nxtxTenant ? isLoadingSessionNxtx : isLoadingSession
                  }
                  stream={nxtxTenant ? streamNxtx : stream}
                  toggleSidebar={toggleSidebar}
                  showSidebar={showSidebar}
                  toggleSummarySidebar={toggleSummarySidebar}
                  showSummarySidebar={showSummarySidebar}
                  toggleQuizSidebar={toggleQuizSidebar}
                  showQuizSidebar={showQuizSidebar}
                  chat={chat}
                  lastDisplayedDate={lastDisplayedDate}
                  currentUser={currentUser}
                  handleCopyText={handleCopyText}
                  copySuccess={copySuccess}
                  chatEndRef={chatEndRef}
                  summaryRecap={summaryRecap}
                  summaryEndRef={summaryEndRef}
                  scrollToTop={scrollToTop}
                  handleBigger={handleBigger}
                  handleSmaller={handleSmaller}
                  volumes={volumes}
                  isVoiceChatActive={isVoiceChatActive}
                  isMuted={isMuted}
                  handleCloseVoiceChat={
                    nxtxTenant ? toggleMute : handleCloseVoiceChat
                  }
                  handleStartVoiceChat={
                    nxtxTenant ? toggleMute : handleStartVoiceChat
                  }
                  disabledMic={disabledMic}
                  micIsLoading={micIsLoading}
                  modeIsLoading={modeIsLoading}
                  remainingDuration={remainingDuration}
                  maxDuration={maxDuration}
                  formatTime={formatTime}
                  showLimitDesc={showLimitDesc}
                  setShowLimitDesc={setShowLimitDesc}
                  isRequestSent={isRequestSent}
                  setIsRequestSent={setIsRequestSent}
                  isIncreaseLimit={isIncreaseLimit}
                  setIsIncreaseLimit={setIsIncreaseLimit}
                  limitChecked={limitChecked}
                  setLimitChecked={setLimitChecked}
                  showAllSidebar={showAllSidebar}
                  setShowAllSidebar={setShowAllSidebar}
                  handleFullScreen={handleFullScreen}
                  showToast={showToast}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleRequestQuota={handleRequestQuota}
                  chatMode={chatMode}
                  isUserTalking={nxtxTenant ? isUserTalkingNxtx : isUserTalking}
                  textValue={textValue}
                  setTextValue={setTextValue}
                  handleSpeakText={handleSpeakText}
                  isLoadingRepeat={isLoadingRepeat}
                  handleChangeChatMode={handleChangeChatMode}
                  saveChat={saveChat}
                  textQuiz={textQuiz}
                  setTextQuiz={setTextQuiz}
                  handleSpeakQuiz={handleSpeakQuiz}
                />
              ) : (
                <AvatarDesktop
                  chatModeTenant={chatModeTenant}
                  nxtxTenant={nxtxTenant}
                  sendInterrupt={sendInterrupt}
                  sendText={sendText}
                  VisualAudioContext={VisualAudioContext}
                  handlePasskeySubmit={handlePasskeySubmit}
                  keywordChipsData={keywordChipsData}
                  isCloseWhenLoading={isCloseWhenLoading}
                  setIsCloseWhenLoading={setIsCloseWhenLoading}
                  isVisible={isVisible}
                  handleInterrupt={nxtxTenant ? sendInterrupt : handleInterrupt}
                  dataAvatar={nxtxTenant ? sessionInfo : dataAvatar}
                  isAnyFiltered={isAnyFiltered}
                  isKeywordsSearched={isKeywordsSearched}
                  isScrolled={isScrolled}
                  handleMinimize={handleMinimize}
                  isSmaller={isSmaller}
                  mediaStream={nxtxTenant ? mediaElementRef : mediaStream}
                  isMediaReady={isMediaReady}
                  setIsMediaReady={setIsMediaReady}
                  startSession={nxtxTenant ? startSessionNxtx : startSession}
                  endSession={nxtxTenant ? endSessionNxtx : endSession}
                  isLoadingSession={
                    nxtxTenant ? isLoadingSessionNxtx : isLoadingSession
                  }
                  stream={nxtxTenant ? streamNxtx : stream}
                  toggleSidebar={toggleSidebar}
                  showSidebar={showSidebar}
                  toggleSummarySidebar={toggleSummarySidebar}
                  showSummarySidebar={showSummarySidebar}
                  toggleQuizSidebar={toggleQuizSidebar}
                  showQuizSidebar={showQuizSidebar}
                  chat={chat}
                  lastDisplayedDate={lastDisplayedDate}
                  currentUser={currentUser}
                  handleCopyText={handleCopyText}
                  copySuccess={copySuccess}
                  chatEndRef={chatEndRef}
                  summaryRecap={summaryRecap}
                  summaryEndRef={summaryEndRef}
                  scrollToTop={scrollToTop}
                  handleBigger={handleBigger}
                  handleSmaller={handleSmaller}
                  volumes={volumes}
                  isVoiceChatActive={isVoiceChatActive}
                  isMuted={isMuted}
                  handleCloseVoiceChat={
                    nxtxTenant ? toggleMute : handleCloseVoiceChat
                  }
                  handleStartVoiceChat={
                    nxtxTenant ? toggleMute : handleStartVoiceChat
                  }
                  disabledMic={disabledMic}
                  micIsLoading={micIsLoading}
                  modeIsLoading={modeIsLoading}
                  remainingDuration={remainingDuration}
                  maxDuration={maxDuration}
                  formatTime={formatTime}
                  showLimitDesc={showLimitDesc}
                  setShowLimitDesc={setShowLimitDesc}
                  isRequestSent={isRequestSent}
                  setIsRequestSent={setIsRequestSent}
                  isIncreaseLimit={isIncreaseLimit}
                  setIsIncreaseLimit={setIsIncreaseLimit}
                  limitChecked={limitChecked}
                  setLimitChecked={setLimitChecked}
                  showAllSidebar={showAllSidebar}
                  setShowAllSidebar={setShowAllSidebar}
                  handleFullScreen={handleFullScreen}
                  showToast={showToast}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  handleRequestQuota={handleRequestQuota}
                  chatMode={chatMode}
                  isUserTalking={nxtxTenant ? isUserTalkingNxtx : isUserTalking}
                  textValue={textValue}
                  setTextValue={setTextValue}
                  handleSpeakText={handleSpeakText}
                  isLoadingRepeat={isLoadingRepeat}
                  handleChangeChatMode={handleChangeChatMode}
                  saveChat={saveChat}
                  textQuiz={textQuiz}
                  setTextQuiz={setTextQuiz}
                  handleSpeakQuiz={handleSpeakQuiz}
                />
              )}
            </>
          )}
      </>
    );
  }
);
